import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getUserProfile } from '../services/api';
import { getCountryFlag, getCountryName } from '../utils/countries';
import { getBadgeDef } from '../utils/badgeDefinitions';
import OctaAvatar from './OctaAvatar';
import { BadgeMedallion } from './BadgeDetailModal';
import '../styles/app/people.css';

// Module-level cache: { [userId]: { data, fetchedAt } }
const profileCache = {};
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getAvatarColor = (userId) => {
  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899'];
  return colors[(userId || 0) % colors.length];
};

function UserHoverCard({ userId, username, children }) {
  const [visible, setVisible] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardStyle, setCardStyle] = useState({});

  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const hoverDelayRef = useRef(null);
  const dismissDelayRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    const cached = profileCache[userId];
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setProfile(cached.data);
      return;
    }

    setLoading(true);
    try {
      const res = await getUserProfile(userId);
      profileCache[userId] = { data: res.data, fetchedAt: Date.now() };
      setProfile(res.data);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      setCardStyle({
        position: 'fixed',
        left: '16px',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        minWidth: 'auto',
        maxWidth: 'none',
        width: 'auto',
      });
    } else {
      const spaceBelow = window.innerHeight - rect.bottom;
      const above = spaceBelow < 300; // card is ~280px tall with badges
      setCardStyle({
        position: 'fixed',
        left: `${rect.left}px`,
        ...(above
          ? { bottom: `${window.innerHeight - rect.top + 8}px`, top: 'auto' }
          : { top: `${rect.bottom + 8}px`, bottom: 'auto' }),
      });
    }
  }, []);

  const showCard = useCallback(() => {
    setVisible(true);
    fetchProfile();
    requestAnimationFrame(updatePosition);
  }, [fetchProfile, updatePosition]);

  const hideCard = useCallback(() => {
    setVisible(false);
  }, []);

  // Desktop: hover with delay
  const handleMouseEnter = useCallback(() => {
    clearTimeout(dismissDelayRef.current);
    hoverDelayRef.current = setTimeout(showCard, 300);
  }, [showCard]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverDelayRef.current);
    dismissDelayRef.current = setTimeout(hideCard, 150);
  }, [hideCard]);

  const handleCardMouseEnter = useCallback(() => {
    clearTimeout(dismissDelayRef.current);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    hideCard();
  }, [hideCard]);

  // Mobile: tap toggle
  const handleClick = useCallback((e) => {
    // Only use tap behavior on touch devices
    if (!('ontouchstart' in window)) return;
    e.preventDefault();
    e.stopPropagation();
    if (visible) {
      hideCard();
    } else {
      showCard();
    }
  }, [visible, showCard, hideCard]);

  // Dismiss on click outside (mobile)
  useEffect(() => {
    if (!visible) return;

    const handleOutsideClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        cardRef.current && !cardRef.current.contains(e.target)
      ) {
        hideCard();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [visible, hideCard]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      clearTimeout(hoverDelayRef.current);
      clearTimeout(dismissDelayRef.current);
    };
  }, []);

  const titleShotBadges = profile?.badges?.filter(b => b.badge_type === 'title_shot_winner') || [];
  const hasBelt = titleShotBadges.length > 0;
  const whiteHouseBadges = profile?.badges?.filter(b => b.badge_type === 'white_house_belt') || [];
  const hasWhiteHouseBelt = whiteHouseBadges.length > 0;

  // Get highest badge from each progressive category
  const getHighestBadge = (prefix) => {
    const earned = (profile?.badges || [])
      .filter(b => b.badge_type?.startsWith(prefix))
      .sort((a, b) => {
        const getNum = (t) => parseInt(t.badge_type.replace(prefix, ''));
        return getNum(b) - getNum(a);
      });
    return earned[0] || null;
  };
  const highestCommBadge = getHighestBadge('commissioner_');
  const highestEventsBadge = getHighestBadge('events_picked_');
  const highestPicksBadge = getHighestBadge('picks_made_');

  // Get best accuracy badge (priority order: full method > full card > main method > main card)
  const accuracyPriority = ['perfect_method_full', 'perfect_card', 'perfect_method_main', 'perfect_main_card'];
  const bestAccuracyBadge = accuracyPriority
    .map(type => (profile?.badges || []).find(b => b.badge_type === type))
    .find(b => b) || null;

  const hasAnyBadge = hasBelt || hasWhiteHouseBelt || highestCommBadge || highestEventsBadge || highestPicksBadge || bestAccuracyBadge;

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const initials = (username || 'U').slice(0, 2).toUpperCase();

  return (
    <span
      ref={triggerRef}
      className="user-hover-trigger"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}

      {visible && ReactDOM.createPortal(
        <div
          ref={cardRef}
          className={`user-hover-card ${hasBelt ? 'uhc-champ' : ''}`}
          style={cardStyle}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
        >
          {loading && !profile ? (
            <div className="uhc-loading" aria-label="Loading...">
              <div className="uhc-loading-row">
                <div className="pnp-skel uhc-skel-avatar" />
                <div className="uhc-loading-lines">
                  <div className="pnp-skel uhc-skel-line" />
                  <div className="pnp-skel uhc-skel-line uhc-skel-line-short" />
                </div>
              </div>
              <span className="sr-only">Loading...</span>
            </div>
          ) : profile ? (
            <>
              {/* Avatar + Username + Country */}
              <div className="uhc-head">
                <div className={`uhc-avatar ${hasBelt ? 'uhc-avatar-gold' : ''}`}>
                  <OctaAvatar
                    initials={initials}
                    name={profile.username}
                    size={48}
                    bg={`linear-gradient(140deg, ${getAvatarColor(userId)}, rgba(6, 9, 18, 0.55))`}
                  />
                </div>
                <div className="uhc-id">
                  <div className="uhc-name-row">
                    <span className="uhc-name">
                      {profile.username}
                    </span>
                    {hasBelt && (
                      <span className="uhc-belt">
                        <img src="/images/title-belt.svg" alt="Belt" />
                        {titleShotBadges.length > 1 && (
                          <span className="uhc-belt-count">x{titleShotBadges.length}</span>
                        )}
                      </span>
                    )}
                    {hasWhiteHouseBelt && (
                      <span className="uhc-belt" title="White House Belt — UFC Freedom 250 winner">
                        <img src="/images/white-house-belt.svg" alt="White House Belt" />
                      </span>
                    )}
                  </div>
                  {profile.country && (
                    <div className="uhc-country">
                      <span className="uhc-flag">{getCountryFlag(profile.country)}</span>
                      <span>{getCountryName(profile.country)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Links */}
              {(profile.instagram || profile.x_handle) && (
                <div className="uhc-socials">
                  {profile.instagram && (
                    <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="uhc-social">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      {profile.instagram}
                    </a>
                  )}
                  {profile.x_handle && (
                    <a href={`https://x.com/${profile.x_handle}`} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="uhc-social">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      {profile.x_handle}
                    </a>
                  )}
                </div>
              )}

              {/* Stats row */}
              <div className="uhc-stats">
                <div className="uhc-stat">
                  <div className="uhc-stat-value">
                    {(profile.totalPicks || 0).toLocaleString()}
                  </div>
                  <div className="uhc-stat-label">
                    Total Picks
                  </div>
                </div>
                <div className="uhc-stat">
                  <div className="uhc-stat-value">
                    {formatJoinDate(profile.created_at)}
                  </div>
                  <div className="uhc-stat-label">
                    Joined
                  </div>
                </div>
              </div>

              {/* Badges */}
              {hasAnyBadge && (
                <div className="uhc-badges">
                  {hasBelt && (
                    <div className="badge-icon-wrapper" data-tooltip={`Title Shot Winner x${titleShotBadges.length}`}
                      onClick={(e) => {
                          e.stopPropagation();
                          document.querySelectorAll('.badge-icon-wrapper.tooltip-visible').forEach(el => { if (el !== e.currentTarget) el.classList.remove('tooltip-visible'); });
                          e.currentTarget.classList.toggle('tooltip-visible');
                        }}>
                      <BadgeMedallion badge={getBadgeDef('title_shot_winner')} earned count={titleShotBadges.length > 1 ? titleShotBadges.length : 0} size={40} />
                      {titleShotBadges.length === 1 && <span className="uhc-badge-count uhc-badge-count-gold">x1</span>}
                    </div>
                  )}
                  {hasWhiteHouseBelt && (
                    <div className="badge-icon-wrapper" data-tooltip="White House Belt — UFC Freedom 250 winner"
                      onClick={(e) => {
                          e.stopPropagation();
                          document.querySelectorAll('.badge-icon-wrapper.tooltip-visible').forEach(el => { if (el !== e.currentTarget) el.classList.remove('tooltip-visible'); });
                          e.currentTarget.classList.toggle('tooltip-visible');
                        }}>
                      <BadgeMedallion badge={getBadgeDef('white_house_belt')} earned size={40} />
                    </div>
                  )}
                  {[highestCommBadge, highestEventsBadge, highestPicksBadge, bestAccuracyBadge].filter(Boolean).map(badge => {
                    const def = getBadgeDef(badge.badge_type);
                    if (!def) return null;
                    const count = def.multi ? (profile?.badges || []).filter(b => b.badge_type === badge.badge_type).length : 0;
                    return (
                      <div key={badge.badge_type} className="badge-icon-wrapper" data-tooltip={`${def.label}${count > 0 ? ` x${count}` : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          document.querySelectorAll('.badge-icon-wrapper.tooltip-visible').forEach(el => { if (el !== e.currentTarget) el.classList.remove('tooltip-visible'); });
                          e.currentTarget.classList.toggle('tooltip-visible');
                        }}>
                        <BadgeMedallion badge={def} earned count={count > 1 ? count : 0} size={40} />
                        {count === 1 && <span className="uhc-badge-count">x1</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="uhc-error">
              <span>Could not load profile</span>
            </div>
          )}
        </div>,
        document.body
      )}
    </span>
  );
}

export default UserHoverCard;
